import React, { useState, useEffect } from 'react';

import Grid from '@mui/material/Grid2';
import { 
  CharacterAdvantage,
} from "@dndModels";
import { 
  DAMAGE_TYPES
} from "@dndModels/Constants";
import { UpsertableBase } from "@models";
import { DataType } from '@dndModels/Types';

import DandDAPI from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import { makeStyles } from "@utilities/theme_utilities";

import ImageFromString from "@dndComponents/display/ImageFromString";
import LoadingBox from "@components/input/LoadingBox";
import InfoButton from "@components/input/buttons/InfoButton";

interface Props {
  name?: string;
  disabled?: boolean;
  for_pdf?: boolean;
  width?: number;
  style?: React.CSSProperties | undefined;
  image?: string;
  find_images?: boolean;
  advantage?: number;
  advantages?: CharacterAdvantage[];
  greater_advantage?: boolean;
  minimum_roll?: number;
  flashing?: boolean;
  log_it?: boolean;
  data_type?: DataType | "";
  id?: string;
  color?: string;
  onClick?: ((event: React.MouseEvent<HTMLDivElement>) => void) | null; 
  split_commas?: boolean;
}

const api = DandDAPI.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

export default function DandDButtonBox({
  name = "",
  disabled = false,
  for_pdf = false,
  width = -1,
  style = {},
  image = "",
  find_images = false,
  advantage = 0,
  advantages = [],
  greater_advantage = false,
  minimum_roll = 0,
  flashing = false,
  log_it = false,
  data_type = "",
  id = "",
  color,
  onClick = null,
  split_commas = false
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [is_flashing, setFlashing] = useState(flashing);
  const [last_advantage, setAdvantage] = useState(advantage);
  // const [last_advantages, setAdvantages] = useState(advantages.map(o => { return {...o}; }));
  const [last_greater_advantage, setGreaterAdvantage] = useState(greater_advantage);
  const [last_minimum_roll, setMinimumRoll] = useState(minimum_roll);
  const [use_name, setUseName] = useState(name);
  const [prev_name, setPrevName] = useState(name);
  const [reloading_name, setReloadingName] = useState(false);
  const [full_obj, setFullObj] = useState<UpsertableBase | null>(null);

  useEffect(() => {
    if (name !== prev_name) {
      // if (name.includes("d4")) {
      //   console.log(name);
      // }
      if (full_obj instanceof UpsertableBase) {
        setUseName(char_util.getStringWithReplacementsWithObject({ clean_me: name, backup: "{obj.use_name}", obj: full_obj }));
      } else {
        setUseName(name);
      }
      setReloadingName(true);
      setPrevName(name);
    }
    // eslint-disable-next-line
  }, [name, prev_name]);
  
  useEffect(() => {
    if (reloading_name) {
      setReloadingName(false);
    }
  }, [reloading_name]);

  // eslint-disable-next-line
  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      
      if (data_type !== "" && id !== "") {
        const clean_me = name === "" ? "{obj.name}" : name;
        setLoading(true);
        api.getFullObject(data_type, id).then((obj: any) => {
          if (obj instanceof UpsertableBase) {
            setUseName(char_util.getStringWithReplacementsWithObject({ clean_me, backup: "{obj.use_name}", obj }));
            setFullObj(obj);
          }
          setLoading(false);
        });
      }
    }
  });

  useEffect(() => {
    setFlashing(flashing);
  }, [flashing]);

  // useEffect(() => {
  //   if (advantagesMatch(last_advantages, advantages)) {
  //     setAdvantages(advantages.map(o => { return {...o}; }));
  //   }
  //   // eslint-disable-next-line
  // }, [advantages]);

  useEffect(() => {
    if (last_advantage !== advantage) {
      setAdvantage(advantage);
    }
    // eslint-disable-next-line
  }, [advantage]);

  useEffect(() => {
    if (last_greater_advantage !== greater_advantage) {
      setGreaterAdvantage(greater_advantage);
    }
    // eslint-disable-next-line
  }, [greater_advantage]);

  useEffect(() => {
    if (last_minimum_roll !== minimum_roll) {
      setMinimumRoll(minimum_roll);
    }
    // eslint-disable-next-line
  }, [minimum_roll]);

  const useStyles = makeStyles()((theme) => {
    if (color) {
      return ({
        root: {
          color: color,
          borderColor: color,
        },
      });
    } else {
      return ({
        root: {
        },
      });
    }
  });

  const renderContent = () => {
    if (split_commas) {
      const pieces = use_name.split(",").map(o => o.trim());
      return pieces.map((piece, key) => {
        return (
          <Grid
            key={key}
            size={{
              xs: 12
            }}>{ renderContent2(piece) }</Grid>
        );
      });
    } else {
      return (
        <Grid
          size={{
            xs: 12
          }}>{ renderContent2(use_name) }</Grid>
      );
    }
  }

  const renderContent2 = (content: string) => {
    const pieces = content.split("|");
    const return_me: any[] = [];
    for (let i = 0; i < pieces.length; ++i) {
      const piece = pieces[i];
      if (find_images) {
        const strs = piece.split(/{|}|,/);
        for (const s of strs) {
          const s2 = s.trim();
          if (s2.length > 0) {
            if ((DAMAGE_TYPES as string[]).includes(s2)) {
              return_me.push(<ImageFromString key={return_me.length} image={s2} />);
            } else {
              return_me.push(<span key={return_me.length}>{s2}&nbsp;</span>);
            }
          }
        }
      } else {
        return_me.push(<span key={return_me.length}>{piece}</span>);
        if (image !== "") {
          return_me.push(<ImageFromString key={return_me.length} image={image} />);
        }
      }
      if (i + 1 < pieces.length) {
        return_me.push(<br key={return_me.length} />);
      }
    }
    // console.log(use_name);
    return return_me;
  }
  
  const { classes, cx } = useStyles();
    
  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    const my_className = `labs_button${is_flashing ? " Flashing" : ""}${disabled ? " disabled" : ""}`;
    const single_row = advantage === 0 && minimum_roll === 0 && !use_name.includes("|"); // advantages.length === 0 && 

    const style2: any = { width: (width > 0 ? `${width}px` : "100%") };
    if (single_row) {
      style2.lineHeight = 2.5;
    }
    if (log_it) {
      debugger;
    }
    return (
      <div style={style2}>
        <Grid container spacing={0} direction="row" className="centered" style={style2}>
          <Grid
            size={{
              xs: 12
            }}>
            <div style={{ ...style, width: "auto" }} 
              className={ for_pdf ? "" : cx(classes.root, my_className)} 
              onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                if (!disabled && !for_pdf && onClick) {
                  onClick(event);
                }
              }}>
              <Grid container spacing={0} direction="row" style={{ height: "100%" }}>
                { renderContent() }
              </Grid>
            </div>
          </Grid>
          { advantage !== 0 && 
            <Grid
              style={{ height: "100%" }}
              size={{
                xs: 12
              }}>
              { advantage > 0 && greater_advantage ? 
                <span style={{ color: "green", fontWeight: "bold" }}>GR. ADV</span> 
              : advantage > 0 ? 
                <span style={{ color: "green" }}>ADV</span> 
              : advantage < 0 && 
                <span style={{ color: "red" }}>DISADV</span> 
              }
              <InfoButton>
                <Grid container spacing={0} direction="row" 
                  style={{ maxHeight: window.innerHeight - 34 }}>
                  <Grid
                    className="header centered"
                    size={{
                      xs: 12
                    }}>
                    Advantage Source(s)
                  </Grid>
                  { advantages.map(a => {
                    return (
                      <Grid
                        key={a.true_id}
                        size={{
                          xs: 12
                        }}>
                        { a.advantage.disadvantage ? "Disadvantage" : "Advantage" }from { a.cfb_name }on { a.source_name }
                      </Grid>
                    );
                  })}
                </Grid>
              </InfoButton>
            </Grid>
          }
          { minimum_roll > 0 && 
            <Grid
              style={{ color: "blue" }}
              size={{
                xs: 12
              }}>
              MIN { minimum_roll }
            </Grid>
          }
        </Grid>
      </div>
    );
  }
}
